import { ArrowRight, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/button'
import { WarningTriangleIcon } from '@/shared/components/icons/warning-triangle'
import type { UserNoticeSource } from '../analytics'
import { useUserNoticeTracking } from '../hooks/use-user-notice-tracking'

interface UserNoticeScreenProps {
  actionUrl: string
  source: UserNoticeSource
  isHard?: boolean
  // Omitted by the dead-end, which renders no close for there to be anything to dismiss.
  onDismiss?: () => void
}

// Full-screen payment-method screen for PayPal payers: warning hero, centered copy
// and a CTA to the payment-method form. SOFT carries a close (×) and its own copy;
// HARD is a dead-end with no way out. Copy is local; the variant and URL are
// server-owned.
export function UserNoticeScreen({
  actionUrl,
  source,
  isHard = false,
  onDismiss = () => {},
}: UserNoticeScreenProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const { onClose, onUpdate } = useUserNoticeTracking({
    source,
    noticeType: isHard ? 'hard' : 'soft',
    actionUrl,
    onDismiss,
  })

  // A dead-end that blocks only visually is not one: Tab would reach the nav behind it and
  // Enter would navigate away. Focus is pulled back and the page is locked from scrolling.
  useEffect(() => {
    const container = containerRef.current
    if (!isHard || !container) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    container.focus()

    const keepFocusInside = (event: FocusEvent) => {
      if (!container.contains(event.target as Node)) container.focus()
    }
    document.addEventListener('focusin', keepFocusInside)

    return () => {
      document.removeEventListener('focusin', keepFocusInside)
      document.body.style.overflow = previousOverflow
    }
  }, [isHard])

  return (
    // Above HelpPopup (z-[60]), which renders after this and would otherwise float on top.
    <div
      ref={containerRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex flex-col bg-white outline-none"
    >
      {/* Phone: CTA pinned to the bottom, thumb-reachable (the Figma layout). Desktop:
          the whole stack is one centered group — stretched apart on a tall window it
          reads as two unrelated screens. */}
      <div className="relative mx-auto flex h-full w-full max-w-lg flex-col sm:justify-center">
        <div className="flex h-11 items-center justify-start px-[16px] pt-[16px] sm:absolute sm:top-0 sm:left-0">
          {!isHard && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('user_notice_close')}
              className="flex size-11 cursor-pointer items-center justify-center text-app-grey-600"
            >
              <X className="h-[18px] w-[15px]" />
            </button>
          )}
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-[24px] overflow-y-auto px-[24px] text-center sm:flex-none sm:gap-[32px]">
          <WarningTriangleIcon className="h-[80px] w-auto" />

          <div className="flex flex-col items-center gap-[12px] sm:gap-[16px]">
            <span className="inline-flex items-center rounded-full border border-app-grey-300 px-[12px] py-[6px] text-sm font-medium text-app-primary-900">
              {t(isHard ? 'user_notice_hard_badge' : 'user_notice_badge')}
            </span>
            <h1 className="font-display font-medium text-[32px] leading-[1.1] text-app-grey-900">
              {t(isHard ? 'user_notice_hard_title' : 'user_notice_title')}
            </h1>
            <p className="text-base leading-6 text-app-grey-700">
              {t('user_notice_subtitle')}
            </p>
          </div>
        </div>

        <div
          className="w-full px-[24px] pt-[8px] sm:pt-[48px]"
          style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
        >
          <p className="pb-[12px] text-center text-sm text-app-grey-600">
            {t('user_notice_reassurance')}
          </p>
          <Button onClick={onUpdate}>
            {t(isHard ? 'user_notice_hard_cta' : 'user_notice_cta')}
            <ArrowRight className="size-5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
